import { TrophyOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_TOP_CONTRIBUTIONS, User } from '@frontend/data-access';
import { formatNumber } from '@frontend/shared/util';
import { Button, List, Select, Typography } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

import UserAvatar from '../user-avatar/user-avatar';

const { Text } = Typography;

type TopFilter = {
  dateRange: string;
  limit: number;
  onlyA3: boolean;
};

type TopContributor = {
  user: User;
  point: number;
};

export function Leaderboard() {
  const { t } = useTranslation('common');
  const { publicRuntimeConfig } = getConfig();
  const [topParams, setTopParams] = useState<TopFilter>({
    dateRange: 'WEEKLY',
    limit: 5,
    onlyA3: false,
  });
  const [getTopUsers, { data, loading }] = useLazyQuery<{
    getTopContributions: TopContributor[];
  }>(GET_TOP_CONTRIBUTIONS);

  const renderImage = (index: number) => {
    if (index > 3) {
      return (
        <div className="relative flex">
          <Image
            src={`${publicRuntimeConfig.staticFolder}/svg/leaderboard/default.svg`}
            height="30"
            width="30"
            alt="badge"
            className="min-w-[30px]"
          />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            {index}
          </div>
        </div>
      );
    } else {
      return (
        <Image
          src={`${publicRuntimeConfig.staticFolder}/svg/leaderboard/place-${index}.svg`}
          height="30"
          width="30"
          alt="badge"
          className="min-w-[30px]"
        />
      );
    }
  };

  useEffect(() => {
    getTopUsers({
      variables: {
        filter: topParams,
      },
    });
  }, [getTopUsers, topParams]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap">
        <h3 className="px-3 text-sm text-gray-900 font-semibold flex gap-2 items-center">
          <TrophyOutlined />
          {t('top_contributors')}
        </h3>
        <Select
          defaultValue="WEEKLY"
          variant={'borderless'}
          style={{ width: 120 }}
          value={topParams.dateRange}
          onChange={(value) =>
            setTopParams({ ...topParams, dateRange: value as string })
          }
          size="small"
        >
          <Select.Option value="ALL">{t('all')}</Select.Option>
          <Select.Option value="MONTHLY">{t('last_month')}</Select.Option>
          <Select.Option value="WEEKLY">{t('last_week')}</Select.Option>
        </Select>
      </div>
      <List
        dataSource={data?.getTopContributions.slice(0, 5)}
        loading={loading}
        renderItem={(item, index) => (
          <div className="flex items-center gap-3 mb-2.5 px-3">
            {renderImage(index + 1)}
            <div className="w-[40px] min-w-[40px]">
              <UserAvatar size={36} user={item?.user} showBadge />
            </div>
            <div className="flex flex-col w-full ">
              <Text className="!text-sm !text-gray-900 !font-medium line-clamp-2">
                {item?.user?.fullName}
              </Text>
              <span className="py-1 text-xs text-gray-700 font-normal whitespace-nowrap">
                {t('number_of_points', { point: formatNumber(item?.point) })}
              </span>
            </div>
          </div>
        )}
      />
      <div>
        <Link href="/leaderboard">
          <Button shape="round" block>
            {t('see_all_contributors')}
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default Leaderboard;
